import { isCompleteRetailScanCode, isPartialInternalScanCode } from './posScanCode.js';

/**
 * Paramètres API pour GET /caisse/retail/retours/search — un mode à la fois (texte ou code).
 * @returns {{ ok: true, params: { q: string, barcode: string }, mode: 'text'|'barcode' } | { ok: false, reason: string }}
 */
export function buildRetailReturnSearchRequest(query, barcode, { preferBarcode = true } = {}) {
  const qTrim = String(query ?? '').trim();
  const bcTrim = String(barcode ?? '').trim();

  if (bcTrim && preferBarcode && isCompleteRetailScanCode(bcTrim)) {
    return { ok: true, mode: 'barcode', params: { q: '', barcode: bcTrim } };
  }

  if (bcTrim && preferBarcode && (isPartialInternalScanCode(bcTrim) || !isCompleteRetailScanCode(bcTrim))) {
    if (qTrim.length >= 2) {
      return { ok: true, mode: 'text', params: { q: qTrim, barcode: '' } };
    }
    return { ok: false, reason: 'barcode_incomplete' };
  }

  if (qTrim.length < 2) {
    return { ok: false, reason: 'query_too_short' };
  }

  return { ok: true, mode: 'text', params: { q: qTrim, barcode: '' } };
}

/** Ignore les réponses HTTP dont la génération de recherche a été remplacée. */
export function isCurrentSearchGeneration(requestSeq, latestSeq) {
  return requestSeq === latestSeq;
}

export function retailReturnSearchHintMessage(hint) {
  switch (hint) {
    case 'QUERY_TOO_SHORT':
      return 'Saisissez au moins 2 caractères ou scannez un code complet';
    case 'BARCODE_NOT_FOUND':
      return 'Aucun produit pour ce code — vérifiez le scan CLKI / EAN';
    case 'BARCODE_INCOMPLETE':
      return null;
    default:
      return null;
  }
}

/** Effets UI lors d'une modification du champ code-barres (invalidation + liste). */
export function retailReturnBarcodeChangeEffects(nextBarcode) {
  const trimmed = String(nextBarcode ?? '').trim();
  const base = { invalidateGeneration: true, clearTickets: true };

  if (
    trimmed &&
    (isPartialInternalScanCode(trimmed) || !isCompleteRetailScanCode(trimmed))
  ) {
    return { ...base, searchStatus: 'typing_barcode' };
  }

  return { ...base, searchStatus: null };
}

/** Effets UI lors d'une modification du champ texte (sans mélange avec le code). */
export function retailReturnQueryChangeEffects() {
  return {
    invalidateGeneration: true,
    clearTickets: true,
    clearBarcode: true,
    searchStatus: null,
  };
}

/**
 * Réponse HTTP encore valide pour l'état courant du formulaire ?
 * @param {{ ok: true, params: { q: string, barcode: string } }} builtAtRequest
 * @param {string} query
 * @param {string} barcode
 * @param {boolean} preferBarcode
 */
export function isRetailReturnSearchResponseStillValid(
  builtAtRequest,
  query,
  barcode,
  preferBarcode
) {
  if (!builtAtRequest?.ok) return false;
  const current = buildRetailReturnSearchRequest(query, barcode, { preferBarcode });
  if (!current.ok) return false;
  return (
    current.mode === builtAtRequest.mode &&
    current.params.q === builtAtRequest.params.q &&
    current.params.barcode === builtAtRequest.params.barcode
  );
}

/**
 * Action de la flèche Retour dans Retour retail (modales → liste → accueil caisse).
 * @returns {'close_consult'|'close_duplicata'|'back_to_list'|'exit_to_caisse'}
 */
export function resolveRetailReturnBackNavigation({
  step,
  consultOpen,
  duplicataOpen,
}) {
  if (consultOpen) return 'close_consult';
  if (duplicataOpen) return 'close_duplicata';
  if (step === 'detail') return 'back_to_list';
  return 'exit_to_caisse';
}
